// Briefings: the twice-daily business briefings (the server works out the
// numbers, the model on server3 writes them up, the bullets go to the phone
// through ntfy), the business context they're judged against, and when they
// run. The open tab lives in the URL (?tab=context) so a reload keeps it.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, API_BASE } from '../lib/auth';
import { Button, Checkbox, Input, PageHeader, Select, Textarea } from '../lib/ui';
import { MODELS, TIME_RE, briefingTitle, cleanTimes, formatWhen, unfilled } from '../lib/briefings';
import { useFlash, errorText, Label } from '../components/TradeCommon';
import BriefingView, { SourcePill } from '../components/BriefingView';
import '../components/Trade.css';
import '../components/Briefings.css';

const TABS = [
  { key: 'briefings', label: 'Briefings' },
  { key: 'context', label: 'Business context' },
  { key: 'schedule', label: 'Schedule and settings' },
];

const SUBTITLES = {
  briefings: 'Written twice a day from the business numbers by the model on server3. The bullets go to your phone',
  context: 'Your profile, goals and priorities in your own words. The briefings judge against these',
  schedule: 'When briefings are written, where the bullets go, and the read-only feed for an assistant',
};

const POLL_MS = 5000;

// ── Briefings ─────────────────────────────────────────────────────────────────

function BriefingsTab({ flash }) {
  const [list, setList] = useState([]);
  const [status, setStatus] = useState('loading');
  const [selected, setSelected] = useState(null);   // id
  const [detail, setDetail] = useState(null);       // the selected briefing with its numbers
  const [writing, setWriting] = useState(null);     // id of a test briefing being written
  const [push, setPush] = useState(false);

  const loadList = useCallback(async () => {
    try {
      const { data } = await api.get('/business/briefings', { params: { limit: 60 } });
      setList(data.briefings);
      setStatus('ready');
      setSelected(id => id || data.briefings[0]?._id || null);
      const inProgress = data.briefings.find(b => b.status === 'running');
      if (inProgress) setWriting(w => w || inProgress._id);
    } catch (e) {
      setStatus(e.response?.status === 404 ? 'missing' : 'error');
    }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  useEffect(() => {
    if (!selected) { setDetail(null); return undefined; }
    let live = true;
    api.get(`/business/briefings/${selected}`)
      .then(({ data }) => { if (live) setDetail(data.briefing); })
      .catch(e => { if (live) flash(errorText(e), 'bad'); });
    return () => { live = false; };
  }, [selected, flash]);

  // Poll the one being written until it's done
  useEffect(() => {
    if (!writing) return undefined;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/business/briefings/${writing}`);
        if (data.briefing.status === 'running') return;
        setWriting(null);
        await loadList();
        setSelected(writing);
        setDetail(data.briefing);
        flash(data.briefing.source === 'ai' ? 'Briefing written' : 'server3 couldn\'t write it, so it\'s the numbers only', data.briefing.source === 'ai' ? 'ok' : 'warn');
      } catch (e) {
        if (e.response?.status === 404) setWriting(null);
      }
    }, POLL_MS);
    return () => clearInterval(t);
  }, [writing, loadList, flash]);

  const runNow = async () => {
    try {
      const { data } = await api.post('/business/briefings/run', { push });
      setWriting(data.briefing._id);
      setList(l => [{ ...data.briefing, bullets: [] }, ...l]);
      setSelected(data.briefing._id);
      flash('Writing a briefing on server3. It takes a few minutes; you can leave this page.', 'info');
    } catch (e) {
      flash(errorText(e), e.response?.status === 409 ? 'warn' : 'bad');
    }
  };

  if (status === 'missing') {
    return <div className="trade-empty"><strong>Not on the server yet</strong>Deploy garageFarmServer to start writing briefings.</div>;
  }

  return (
    <>
      <div className="br-toolbar">
        <Button variant="primary" onClick={runNow} disabled={!!writing}>{writing ? 'Writing…' : 'Write a test briefing now'}</Button>
        <Checkbox checked={push} onChange={e => setPush(e.target.checked)} label="Send it to my phone too" />
      </div>
      {status === 'error' && <div className="ui-banner ui-banner--bad">Couldn't load the briefings.</div>}
      {status === 'ready' && !list.length && (
        <div className="trade-empty"><strong>No briefings yet</strong>The first is written at the next time set under Schedule and settings.</div>
      )}
      {list.length > 0 && (
        <div className="br-layout">
          <div className="br-list">
            {list.map(b => (
              <button key={b._id} type="button" className={`br-item${b._id === selected ? ' is-active' : ''}`} onClick={() => setSelected(b._id)}>
                <span className="br-item__top">{briefingTitle(b)} <SourcePill briefing={b} /></span>
                <span className="br-item__when">{formatWhen(b.startedAt)}</span>
                {b.bullets?.[0] && <span className="br-item__first">{b.bullets[0]}</span>}
              </button>
            ))}
          </div>
          <div className="ui-card br-detail">
            {detail && detail._id === selected ? (
              <>
                <div className="br-detail__head">
                  <h2>{briefingTitle(detail)}</h2>
                  <span className="br-card__when">{formatWhen(detail.startedAt)}</span>
                  <SourcePill briefing={detail} />
                </div>
                <BriefingView briefing={detail} showNumbers />
              </>
            ) : <p className="br-card__empty">Loading…</p>}
          </div>
        </div>
      )}
    </>
  );
}

// ── Business context ──────────────────────────────────────────────────────────

const HINTS = {
  about: 'Who you are, what the business does and how it sells',
  goals: 'Targets with dates. Replace each [placeholder] with your own: the briefings judge against these',
  priorities: 'What matters most this month and this week. Update it as things change',
  howToWork: 'How the briefings (and later the assistant) should talk to you',
};
const ROWS = { about: 20, goals: 7, priorities: 5, howToWork: 7 };

function ContextTab({ flash, dirtyRef }) {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [revision, setRevision] = useState('');

  useEffect(() => {
    api.get('/business/context')
      .then(({ data: d }) => { setData(d); setDraft(d.context); })
      .catch(e => flash(errorText(e), 'bad'));
  }, [flash]);

  const keys = data ? data.sections.map(s => s.key) : [];
  const dirty = !!draft && keys.some(k => draft[k] !== data.context[k]);
  useEffect(() => { dirtyRef.current = dirty; }, [dirty, dirtyRef]);

  if (!data || !draft) return <p className="br-card__empty">Loading…</p>;

  const save = async () => {
    setSaving(true);
    try {
      const { data: d } = await api.put('/business/context', Object.fromEntries(keys.map(k => [k, draft[k]])));
      setData(prev => ({ ...prev, context: d.context, revisions: d.revisions }));
      setDraft(d.context);
      setRevision('');
      flash('Saved. The next briefing will use it.');
    } catch (e) {
      flash(errorText(e), 'bad');
    }
    setSaving(false);
  };

  const loadRevision = () => {
    const r = data.revisions[Number(revision)];
    if (r) setDraft(Object.fromEntries(keys.map(k => [k, r[k] || ''])));
  };

  const gaps = unfilled(draft, data.sections);

  return (
    <div className="br-context">
      {gaps.length > 0 && (
        <div className="ui-banner ui-banner--info">
          {gaps.join(' and ')} still {gaps.length === 1 ? 'has' : 'have'} [placeholders]. Until they're filled in, the briefings treat those goals as not set.
        </div>
      )}
      {data.sections.map(s => (
        <div key={s.key}>
          <Label hint={HINTS[s.key]}>{s.title}</Label>
          <Textarea rows={ROWS[s.key] || 6} value={draft[s.key]} onChange={e => setDraft(d => ({ ...d, [s.key]: e.target.value }))} />
        </div>
      ))}
      <div className="br-toolbar" style={{ justifyContent: 'space-between' }}>
        <span className="trade-form__hint">
          {data.context.updatedAt ? `Last saved ${formatWhen(data.context.updatedAt)}` : 'Not edited yet: this is the starting profile'}
          {dirty ? ' · unsaved changes' : ''}
        </span>
        <div className="br-toolbar" style={{ margin: 0 }}>
          {data.revisions.length > 0 && (
            <>
              <Select value={revision} onChange={e => setRevision(e.target.value)} style={{ width: 'auto' }}>
                <option value="">Earlier versions…</option>
                {data.revisions.map((r, i) => (
                  <option key={i} value={i}>{new Date(r.at).getTime() > 0 ? `As saved ${formatWhen(r.at)}` : 'The starting profile'}</option>
                ))}
              </Select>
              <Button variant="muted" onClick={loadRevision} disabled={revision === ''}>Load into the editor</Button>
            </>
          )}
          {dirty && <Button variant="muted" onClick={() => setDraft(data.context)}>Undo changes</Button>}
          <Button variant="primary" onClick={save} disabled={!dirty || saving}>{saving ? 'Saving…' : 'Save'}</Button>
        </div>
      </div>
    </div>
  );
}

// ── Schedule and settings ─────────────────────────────────────────────────────

const ASSISTANT_URL = `${API_BASE}/business/assistant`;

const numOrNull = (v) => (v === '' || v === null || v === undefined ? null : Number(v));

function toForm(s) {
  return {
    enabled: s.briefings.enabled,
    times: s.briefings.times.length ? s.briefings.times : ['06:30'],
    model: s.briefings.model,
    push: s.briefings.push,
    topic: s.briefings.topic,
    revenueTargetGrowth: s.revenueTargetGrowth ?? '',
    tempMin: s.climate?.tempMin ?? '', tempMax: s.climate?.tempMax ?? '',
    humidityMin: s.climate?.humidityMin ?? '', humidityMax: s.climate?.humidityMax ?? '',
  };
}

function ScheduleTab({ flash }) {
  const [settings, setSettings] = useState(null);
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [token, setToken] = useState('');

  useEffect(() => {
    api.get('/business/settings')
      .then(({ data }) => { setSettings(data.settings); setF(toForm(data.settings)); })
      .catch(e => flash(errorText(e), 'bad'));
  }, [flash]);

  if (!settings || !f) return <p className="br-card__empty">Loading…</p>;

  const set = (k) => (e) => setF(p => ({ ...p, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const setTime = (i) => (e) => setF(p => ({ ...p, times: p.times.map((t, j) => (j === i ? e.target.value : t)) }));
  const badTimes = f.times.filter(t => !TIME_RE.test(t));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch('/business/settings', {
        briefings: { enabled: f.enabled, times: cleanTimes(f.times), model: f.model, push: f.push, topic: f.topic },
        revenueTargetGrowth: numOrNull(f.revenueTargetGrowth),
        climate: { tempMin: numOrNull(f.tempMin), tempMax: numOrNull(f.tempMax), humidityMin: numOrNull(f.humidityMin), humidityMax: numOrNull(f.humidityMax) },
      });
      setSettings(data.settings);
      setF(toForm(data.settings));
      flash('Saved');
    } catch (e) {
      flash(errorText(e), 'bad');
    }
    setSaving(false);
  };

  const makeToken = async () => {
    if (settings.assistantToken.exists && !window.confirm('Make a new token? The old one stops working straight away.')) return;
    try {
      const { data } = await api.post('/business/assistant-token');
      setToken(data.token);
      setSettings(data.settings);
    } catch (e) { flash(errorText(e), 'bad'); }
  };

  const revokeToken = async () => {
    if (!window.confirm('Revoke the assistant token? Anything using it stops working.')) return;
    try {
      const { data } = await api.delete('/business/assistant-token');
      setToken('');
      setSettings(data.settings);
    } catch (e) { flash(errorText(e), 'bad'); }
  };

  const copyToken = async () => {
    try { await navigator.clipboard.writeText(token); flash('Token copied'); } catch { flash('Copy it by hand: select the text', 'warn'); }
  };

  const t = settings.assistantToken;

  return (
    <div className="br-settings">
      <div className="trade-form">
        <h4>When</h4>
        <Checkbox checked={f.enabled} onChange={set('enabled')} label="Write briefings automatically" />
        <div>
          <Label hint="UK time; before noon is the morning briefing">Times</Label>
          <div className="br-times">
            {f.times.map((time, i) => (
              <span key={i} className="br-times">
                <Input type="time" value={time} onChange={setTime(i)} invalid={!TIME_RE.test(time)} />
                {f.times.length > 1 && <Button size="sm" variant="muted" onClick={() => setF(p => ({ ...p, times: p.times.filter((_, j) => j !== i) }))}>Remove</Button>}
              </span>
            ))}
            {f.times.length < 4 && <Button size="sm" onClick={() => setF(p => ({ ...p, times: [...p.times, '12:00'] }))}>Add a time</Button>}
          </div>
          <div className="trade-form__hint" style={{ marginTop: 6 }}>Each covers the time since the one before. If the server is restarting at that moment, it still runs up to 45 minutes late.</div>
        </div>

        <h4>Model</h4>
        <div>
          <Label hint="on server3">Ollama model</Label>
          <Input value={f.model} onChange={set('model')} list="br-models" />
          <datalist id="br-models">{MODELS.map(m => <option key={m} value={m} />)}</datalist>
          <div className="trade-form__hint" style={{ marginTop: 6 }}>qwen3.6:35b-a3b thinks before it writes, so a briefing takes a few minutes. If server3 can't be reached, the phone gets the numbers instead.</div>
        </div>

        <h4>Phone</h4>
        <Checkbox checked={f.push} onChange={set('push')} label="Push the bullets to ntfy" />
        <div>
          <Label hint="on push.teardrophydro.co.uk">Topic</Label>
          <Input value={f.topic} onChange={set('topic')} />
          <div className="trade-form__hint" style={{ marginTop: 6 }}>
            Once, on server2: <span className="br-code">sudo ntfy access admin {f.topic || 'teardrop-briefing'} rw</span>, then subscribe to <b>{f.topic || 'teardrop-briefing'}</b> in the ntfy app. Pushes carry no customer details.
          </div>
        </div>

        <h4>What the briefings judge against</h4>
        <div>
          <Label hint="shared with the Sales page">Monthly revenue target growth</Label>
          <Input type="number" min="0.5" max="5" step="0.05" value={f.revenueTargetGrowth} onChange={set('revenueTargetGrowth')} placeholder="1.5" style={{ maxWidth: 140 }} />
          <div className="trade-form__hint" style={{ marginTop: 6 }}>The Sales page target: July 2026's revenue, multiplied by this for each month since. Empty means 1.5.</div>
        </div>
        <div className="inv-grid2">
          <div>
            <Label hint="°C, lowest and highest">Room temperature</Label>
            <div className="br-times">
              <Input type="number" step="0.5" value={f.tempMin} onChange={set('tempMin')} placeholder="low" />
              <Input type="number" step="0.5" value={f.tempMax} onChange={set('tempMax')} placeholder="high" />
            </div>
          </div>
          <div>
            <Label hint="%, lowest and highest">Humidity</Label>
            <div className="br-times">
              <Input type="number" step="1" value={f.humidityMin} onChange={set('humidityMin')} placeholder="low" />
              <Input type="number" step="1" value={f.humidityMax} onChange={set('humidityMax')} placeholder="high" />
            </div>
          </div>
        </div>
        <div className="trade-form__hint">Readings outside these ranges are counted in the briefing. Leave them empty and the briefings only report the ranges.</div>

        <div className="trade-form__actions">
          <Button variant="muted" onClick={() => setF(toForm(settings))}>Undo changes</Button>
          <Button variant="primary" onClick={save} disabled={saving || badTimes.length > 0}>{saving ? 'Saving…' : 'Save'}</Button>
        </div>

        <h4>Assistant feed</h4>
        <div className="trade-form__hint">
          A read-only feed of the business context and the current numbers (no customer details) for a tool in Open WebUI: <span className="br-code">GET {ASSISTANT_URL}</span> with the header <span className="br-code">Authorization: Bearer &lt;token&gt;</span>. Add <span className="br-code">?hours=24</span> for a fixed period.
        </div>
        <div className="trade-form__hint">
          {t.exists
            ? `Token made ${formatWhen(t.createdAt)}${t.lastUsedAt ? `, last used ${formatWhen(t.lastUsedAt)}` : ', not used yet'}.`
            : 'No token yet.'}
        </div>
        {token && (
          <div>
            <div className="br-token">{token}</div>
            <div className="trade-form__hint" style={{ marginTop: 6 }}>Copy it now: it isn't shown again. <Button size="sm" onClick={copyToken}>Copy</Button></div>
          </div>
        )}
        <div className="br-toolbar" style={{ margin: 0 }}>
          <Button onClick={makeToken}>{t.exists ? 'Make a new token' : 'Make a token'}</Button>
          {t.exists && <Button variant="danger" onClick={revokeToken}>Revoke</Button>}
        </div>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function BriefingsPage() {
  const [params, setParams] = useSearchParams();
  const keys = TABS.map(t => t.key);
  const tab = keys.includes(params.get('tab')) ? params.get('tab') : 'briefings';
  const { flash, banner } = useFlash();
  const contextDirty = useRef(false);

  const onTabChange = (t) => {
    if (tab === 'context' && t !== 'context' && contextDirty.current && !window.confirm('Leave without saving your changes to the business context?')) return;
    contextDirty.current = false;
    setParams(t === 'briefings' ? {} : { tab: t }, { replace: true });
  };

  return (
    <div className="dashboard-container">
      <PageHeader title="Briefings" subtitle={SUBTITLES[tab]} tabs={TABS} activeTab={tab} onTabChange={onTabChange} />
      {banner}
      {tab === 'briefings' && <BriefingsTab flash={flash} />}
      {tab === 'context' && <ContextTab flash={flash} dirtyRef={contextDirty} />}
      {tab === 'schedule' && <ScheduleTab flash={flash} />}
    </div>
  );
}
